import JSZip from "jszip"
import { saveAs } from "file-saver"

export type fileDetails = {
  filename: string
  url: string
}

const mimeToExt: Record<string, string> = {
  "image/jpeg": ".jpg",
  "image/jpg": ".jpg",
  "image/png": ".png",
  "image/gif": ".gif",
  "video/mp4": ".mp4",
  "video/webm": ".webm",
  "text/html": ".html",
  // Add more as needed
}

export function downloadToUser(data: Blob, defaultFileName: string) {
  const link = document.createElement("a")
  link.style.display = "none"
  document.body.appendChild(link)
  link.href = window.URL.createObjectURL(new Blob([data]))
  link.setAttribute("download", defaultFileName)
  link.click()
  window.URL.revokeObjectURL(link.href)
  document.body.removeChild(link)
}

export function downloadS3AsBlob(url: string, fileName: string) {
  return fetch(url, {
    method: "GET",
    headers: { "Cache-Control": "no-cache" },
  })
    .then((response) => response.blob())
    .then((blob) => {
      downloadToUser(blob, fileName)
    })
    .catch((err) => console.log(err))
}

export const downloadFilesAsZip = async (data: fileDetails[]) => {
  const zip = new JSZip()

  const filePromises = data.map(async (file) => {
    const response = await fetch(file.url, {
      method: "GET",
      headers: { "Cache-Control": "no-cache" },
    })
    const blob = await response.blob()
    zip.file(file.filename, blob)
  })

  await Promise.all(filePromises)

  const zipBlob = await zip.generateAsync({ type: "blob" })
  saveAs(zipBlob, "creatives.zip")
}

export const getInitials = (str: string) => {
  const words = str.split(" ")

  if (words.length === 1) {
    const word = words[0]
    return word[0].toUpperCase() + word[word.length - 1].toUpperCase()
  }
  return words.map((word) => word[0].toUpperCase()).join("")
}

export const createCustomName = (
  fileType: string,
  tagData: Record<string, string | string[]>,
  assetCategory: string,
) => {
  let nameStr = ""
  const fileExtension = mimeToExt[fileType]

  const getTagValue = (key: string, joinWith = "_") => {
    const value = tagData[key]
    if (Array.isArray(value)) {
      return value.join(joinWith)
    }
    return value || "_"
  }

  switch (assetCategory) {
    case "Ads - Video":
      nameStr += getInitials(getTagValue("Game"))
      nameStr += `_${getTagValue("Asset Type ID")}`
      nameStr += `_${getTagValue("Concept").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Motivator").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Language")}`
      nameStr += `_${getTagValue("Length")}`
      nameStr += `_${getTagValue("Dimensions")}`
      break

    case "Ads - Image":
      nameStr += getInitials(getTagValue("Game"))
      nameStr += `_${getTagValue("Asset Type ID")}`
      nameStr += `_${getTagValue("Concept").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Motivator").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Language")}`
      nameStr += `_${getTagValue("Length")}`
      nameStr += `_${getTagValue("Dimensions")}`
      break

    case "Ads - HTML":
      nameStr += getInitials(getTagValue("Game"))
      nameStr += `_${getTagValue("Asset Type ID")}`
      nameStr += `_${getTagValue("Concept").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Motivator").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Language")}`
      nameStr += `_${getTagValue("Length")}`
      nameStr += `_${getTagValue("Dimensions")}`
      break

    case "Web/Socials - Video":
      nameStr += getInitials(getTagValue("Game"))
      nameStr += `_${getTagValue("Asset Type ID")}`
      nameStr += `_${getTagValue("Task Type ID")}`
      nameStr += `_${getTagValue("Concept").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Device or Platform Name")}`
      nameStr += `_${getTagValue("Length")}`
      nameStr += `_${getTagValue("Dimensions")}`
      nameStr += `_${getInitials(getTagValue("Orientation"))}`
      nameStr += `_${getTagValue("Language")}`
      break

    case "Web/Socials - Image":
      nameStr += getInitials(getTagValue("Game"))
      nameStr += `_${getTagValue("Asset Type ID")}`
      nameStr += `_${getTagValue("Task Type ID")}`
      nameStr += `_${getTagValue("Concept").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Device or Platform Name")}`
      nameStr += `_${getTagValue("Length")}`
      nameStr += `_${getTagValue("Dimensions")}`
      nameStr += `_${getInitials(getTagValue("Orientation"))}`
      nameStr += `_${getTagValue("Language")}`
      break

    case "ASO - Video":
      nameStr += getInitials(getTagValue("Game"))
      nameStr += `_${getTagValue("Asset Type ID")}`
      nameStr += `_${getTagValue("Task Type ID")}`
      nameStr += `_${getTagValue("Concept").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Device or Platform Name")}`
      nameStr += `_${getTagValue("Length")}`
      nameStr += `_${getTagValue("Dimensions")}`
      nameStr += `_${getInitials(getTagValue("Orientation"))}`
      nameStr += `_${getTagValue("Language")}`
      break

    case "ASO - Image":
      nameStr += getInitials(getTagValue("Game"))
      nameStr += `_${getTagValue("Asset Type ID")}`
      nameStr += `_${getTagValue("Task Type ID")}`
      nameStr += `_${getTagValue("Concept").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Device or Platform Name")}`
      nameStr += `_${getTagValue("Length")}`
      nameStr += `_${getTagValue("Dimensions")}`
      nameStr += `_${getInitials(getTagValue("Orientation"))}`
      nameStr += `_${getTagValue("Language")}`
      break

    default:
      nameStr += getInitials(getTagValue("Game"))
      nameStr += `_${getTagValue("Asset Type ID")}`
      nameStr += `_${getInitials(getTagValue("Artist"))}`
      nameStr += `_${getTagValue("Concept").replaceAll(" ", "_")}`
      nameStr += `_${getTagValue("Length")}`
      nameStr += `_${getTagValue("Dimensions")}`
      nameStr += `_${getTagValue("Language")}`
      break
  }

  return `${nameStr}${fileExtension}`
}
